<section class="import-panel">
  <header class="panel-header">
    <div>
      <h2>{{ kindLabel }}の入稿</h2>
      <p>
        社員×年月単位の{{ acceptFileExtensions[0].toUpperCase() }}ファイルを
        取り込みます。同じ社員・年月のデータは上書きされます。
      </p>
    </div>
    <span class="unit-chip">単位：社員 × 年月</span>
  </header>

  <section
    class="imported-summary"
    aria-labelledby="imported-summary-heading-{{ kind | lowercase }}"
    [attr.aria-busy]="isLoadingSummaries ? 'true' : null"
  >
    <h3 id="imported-summary-heading-{{ kind | lowercase }}">
      取込済みの{{ kindLabel }}
    </h3>
    @if (summaryLoadFailed) {
      <div class="summary-error-actions">
        <p class="summary-error" role="alert">{{ summaryLoadErrorMessage }}</p>
        <button
          mat-stroked-button
          type="button"
          [disabled]="isLoadingSummaries"
          (click)="loadSummaries()"
        >
          再試行
        </button>
      </div>
    }
    @if (isLoadingSummaries && summaries.length === 0) {
      <div class="summary-loading" role="status">
        <mat-spinner diameter="18" aria-hidden="true"></mat-spinner>
        <span>読み込み中...</span>
      </div>
    } @else if (summaries.length > 0) {
      <div class="preview-table-wrap">
        <table>
          <caption class="visually-hidden">
            取込済みの{{
              kindLabel
            }}の年月別一覧
          </caption>
          <thead>
            <tr>
              <th scope="col">対象年月</th>
              <th scope="col">登録人数</th>
              <th scope="col">最終更新日時</th>
            </tr>
          </thead>
          <tbody>
            @for (summary of summaries; track summary.period) {
              <tr>
                <td>{{ summary.period }}</td>
                <td>{{ summary.recordCount }}人</td>
                <td>{{ summary.lastUpdatedAt | date: 'yyyy/MM/dd HH:mm' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else if (!summaryLoadFailed) {
      <p class="empty-note">取込済みの{{ kindLabel }}はありません。</p>
    }
  </section>

  <div class="import-controls">
    <div class="period-field">
      <label [for]="periodInputId">対象年月</label>
      <input
        [id]="periodInputId"
        type="month"
        [(ngModel)]="period"
        [disabled]="isBusy"
        required
        (ngModelChange)="onPeriodChanged()"
      />
    </div>

    @if (canDownloadTemplate) {
      <button
        mat-stroked-button
        type="button"
        [disabled]="isBusy"
        (click)="downloadTemplate()"
      >
        @if (isDownloadingTemplate) {
          <mat-spinner diameter="18"></mat-spinner>
        } @else {
          <mat-icon>download</mat-icon>
        }
        テンプレートをダウンロード
      </button>
    }
  </div>

  <fieldset class="uploader-fieldset" [disabled]="isBusy">
    <legend class="visually-hidden">{{ kindLabel }}ファイルの選択</legend>
    <app-file-uploader
      [acceptFileExtensions]="acceptFileExtensions"
      (uploaded)="onFilesSelected($event)"
    />
  </fieldset>

  <div class="file-status" aria-live="polite">
    @if (isPreviewing) {
      <mat-spinner diameter="18" aria-hidden="true"></mat-spinner>
    } @else {
      <mat-icon aria-hidden="true">description</mat-icon>
    }
    @if (selectedFile) {
      <span>
        {{ selectedFile.name }}（{{ formatFileSize(selectedFile.size) }}）{{
          isPreviewing ? 'をプレビュー中...' : ''
        }}
      </span>
    } @else {
      <span>ファイルは選択されていません（最大10MB）</span>
    }
  </div>

  @if (isRecord) {
    <p class="helper-note">
      KPI の率ではなく、各項目の分子・分母を入稿します。率は AI Sales Coach
      側で算出します。
    </p>
  } @else {
    <p class="helper-note">
      KPI 項目ごとの目標率をパーセント値で入稿します。空欄または 0
      の項目は目標未設定として保存され、再入稿時は該当項目の既存目標が解除されます。全項目を空欄にした行は、そのオペレーターのその年月の目標をすべて解除します。
    </p>
  }

  <div class="primary-actions">
    <button
      mat-flat-button
      color="primary"
      type="button"
      [disabled]="!canConfirmImport"
      (click)="confirmImport()"
    >
      @if (isImporting) {
        <mat-spinner diameter="18"></mat-spinner>
      } @else {
        <mat-icon>upload_file</mat-icon>
      }
      {{ isImporting ? '取込中...' : '取込を確定' }}
    </button>
  </div>

  @if (operationMessage) {
    <div class="operation-result" role="status">
      <mat-icon aria-hidden="true">check_circle</mat-icon>
      {{ operationMessage }}
    </div>
  }

  @if (confirmedResult) {
    <section class="confirmed-summary" aria-label="取込結果">
      <h3>取込結果</h3>
      <p>
        総行数 {{ confirmedResult.totalRows }}件のうち
        <strong>{{ confirmedResult.importedRows }}件</strong>を取り込みました。
        エラーは {{ confirmedResult.errorCount }}件です。
      </p>
      @if (confirmedErrors.length > 0) {
        <ul>
          @for (error of confirmedErrors; track $index) {
            <li>
              <strong>{{ formatErrorRow(error) }}</strong>
              <span>{{ error.message }}</span>
            </li>
          }
        </ul>
      }
      @if (confirmedResult.omittedErrors > 0) {
        <p class="omitted-note error-omitted-note">
          他 {{ confirmedResult.omittedErrors }} 件のエラーは省略されています
        </p>
      }
    </section>
  }

  @if (previewResult) {
    <section class="preview-section" aria-labelledby="preview-heading">
      <div class="preview-heading">
        <div>
          <h3 id="preview-heading">取込プレビュー</h3>
          <p>エラー行を除く突合成功行のみ取り込まれます。</p>
        </div>
        <div class="summary-chips" aria-label="プレビュー集計">
          <span>総行数 {{ previewResult.totalRows }}件</span>
          <span class="success">
            取込予定 {{ previewResult.importedRows }}件
          </span>
          <span class="error">エラー {{ previewResult.errorCount }}件</span>
        </div>
      </div>

      @if (previewDisplayRows.length > 0) {
        <div class="preview-table-wrap">
          <table>
            <caption class="visually-hidden">
              {{
                kindLabel
              }}の取込予定行
            </caption>
            <thead>
              @if (isRecord) {
                <tr>
                  <th scope="col" rowspan="2">社員番号</th>
                  <th scope="col" rowspan="2">氏名</th>
                  <th scope="col" rowspan="2">役職</th>
                  @for (item of dashboard.items; track item.id) {
                    <th scope="colgroup" colspan="2">{{ item.kpiName }}</th>
                  }
                </tr>
                <tr>
                  @for (item of dashboard.items; track item.id) {
                    <th scope="col">{{ item.denominatorName }}</th>
                    <th scope="col">{{ item.numeratorName }}</th>
                  }
                </tr>
              } @else {
                <tr>
                  <th scope="col">社員番号</th>
                  <th scope="col">氏名</th>
                  @for (item of dashboard.items; track item.id) {
                    <th scope="col">{{ item.kpiName }}（%）</th>
                  }
                </tr>
              }
            </thead>
            <tbody>
              @for (row of previewDisplayRows; track row.employeeNumber) {
                <tr>
                  <td>{{ row.employeeNumber }}</td>
                  <td>
                    <span class="user-name">{{ row.userName }}</span>
                    @if (row.fileDisplayName) {
                      <span class="file-display-name">
                        ファイル記載：{{ row.fileDisplayName }}
                      </span>
                    }
                  </td>
                  @if (isRecord) {
                    <td>{{ row.roleTitle }}</td>
                  }
                  @for (value of row.values; track $index) {
                    <td>{{ value }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <p class="no-preview-rows">取り込み可能な行はありません。</p>
      }
      @if (previewResult.omittedPreviewRows > 0) {
        <p class="omitted-note preview-omitted-note">
          他
          {{ previewResult.omittedPreviewRows }} 行は省略されています（取込予定
          {{ previewResult.importedRows }} 行すべてが確定対象です）
        </p>
      }

      @if (previewErrors.length > 0 || previewResult.omittedErrors > 0) {
        <section class="error-list" aria-labelledby="error-list-heading">
          <h4 id="error-list-heading">エラー行一覧</h4>
          <ul>
            @for (error of previewErrors; track $index) {
              <li>
                <strong>{{ formatErrorRow(error) }}</strong>
                <span>{{ error.message }}</span>
              </li>
            }
          </ul>
          @if (previewResult.omittedErrors > 0) {
            <p class="omitted-note error-omitted-note">
              他 {{ previewResult.omittedErrors }} 件のエラーは省略されています
            </p>
          }
        </section>
      }
    </section>
  }

  <footer class="danger-zone">
    <div>
      <h3>指定年月のデータを一括削除</h3>
      <p>{{ period }} に登録済みの{{ kindLabel }}をすべて削除します。</p>
    </div>
    <button
      mat-stroked-button
      type="button"
      class="delete-button"
      [disabled]="!isPeriodValid || isBusy"
      (click)="deleteMonthlyData()"
    >
      @if (isDeleting) {
        <mat-spinner diameter="18"></mat-spinner>
      } @else {
        <mat-icon>delete_outline</mat-icon>
      }
      {{ isDeleting ? '削除中...' : '一括削除' }}
    </button>
  </footer>
</section>
